import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { Terrain } from '../game/Terrain';
import { readTune } from '../ui/TuneStore';
import type { GrassPlacement } from '../ui/WorldStore';

import { assetUrl as _assetUrl } from '../ui/AssetMap';
const MODEL_SLOT = 'grass-tuft';
const MODEL_URL_DEFAULT = '/assets/grass-tuft.glb';

interface GrassOptions {
  count: number;
  /** Camera distance beyond which instances fade to zero size. */
  maxDistance?: number;
  /** Pre-computed placements from the shared WorldStore (same across clients). */
  placements?: GrassPlacement[];
}

/**
 * Instanced GLB grass tufts. Scale + Y offset come from the shared TuneStore
 * ('/api/tune' entry "grass-tuft"). Wind sway + camera-distance fade are in
 * the vertex shader — fresh material, not cloned from the GLB's.
 */
export class Grass {
  mesh!: THREE.InstancedMesh;
  generatedPlacements: GrassPlacement[] = [];
  private uTime!: { value: number };
  private uCamPos!: { value: THREE.Vector3 };
  private uWindMul!: { value: number };

  constructor(private terrain: Terrain, private opts: GrassOptions) {}

  async load() {
    const gltf = await new GLTFLoader().loadAsync(_assetUrl(MODEL_SLOT, MODEL_URL_DEFAULT));
    let src: THREE.Mesh | null = null;
    gltf.scene.updateMatrixWorld(true);
    gltf.scene.traverse((o) => {
      if (!src && (o as THREE.Mesh).isMesh) src = o as THREE.Mesh;
    });
    if (!src) throw new Error('grass GLB has no mesh');
    const srcMesh: THREE.Mesh = src;

    const geometry = this.stripToBasics((srcMesh.geometry as THREE.BufferGeometry).clone());
    geometry.applyMatrix4(srcMesh.matrixWorld);
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();

    const mat = new THREE.MeshStandardMaterial({
      color: 0x4a6b28,
      roughness: 0.85,
      metalness: 0,
      side: THREE.DoubleSide,
    });

    const maxDist = this.opts.maxDistance ?? 22;
    const fadeBand = 4.0;

    this.uTime = { value: 0 };
    this.uCamPos = { value: new THREE.Vector3() };
    this.uWindMul = { value: 1 };

    mat.onBeforeCompile = (shader) => {
      shader.uniforms.uTime = this.uTime;
      shader.uniforms.uCamPos = this.uCamPos;
      shader.uniforms.uWindMul = this.uWindMul;
      shader.uniforms.uMaxDist = { value: maxDist };
      shader.uniforms.uFadeBand = { value: fadeBand };

      shader.vertexShader = shader.vertexShader
        .replace(
          '#include <common>',
          `#include <common>
           uniform float uTime;
           uniform vec3 uCamPos;
           uniform float uWindMul;
           uniform float uMaxDist;
           uniform float uFadeBand;`,
        )
        .replace(
          '#include <begin_vertex>',
          `#include <begin_vertex>
           vec3 iPos = vec3(instanceMatrix[3][0], instanceMatrix[3][1], instanceMatrix[3][2]);
           float camDist = distance(iPos, uCamPos);
           float vis = 1.0 - smoothstep(uMaxDist - uFadeBand, uMaxDist, camDist);
           // Top-of-tuft sway.  Use y relative to the tuft's own bounding box.
           float tipT = clamp(position.y / 1.0, 0.0, 1.0);
           float phase = iPos.x * 0.7 + iPos.z * 0.5;
           float wind = sin(uTime * 1.6 + phase) * 0.6 + sin(uTime * 3.1 + phase * 2.1) * 0.4;
           float bend = wind * 0.09 * tipT * tipT * uWindMul;
           transformed.x += bend;
           transformed.z += bend * 0.3;
           transformed.y = mix(0.0, transformed.y, vis);
           transformed.x *= vis;
           transformed.z *= vis;`,
        );
    };

    this.mesh = new THREE.InstancedMesh(geometry, mat, this.opts.count);
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = true;
    this.mesh.frustumCulled = false;

    this.scatter();
  }

  update(elapsed: number, camera?: THREE.Camera, windMul = 1) {
    this.uTime.value = elapsed;
    if (camera) this.uCamPos.value.copy(camera.position);
    this.uWindMul.value = windMul;
  }

  private stripToBasics(g: THREE.BufferGeometry): THREE.BufferGeometry {
    const keep = new Set(['position', 'normal', 'uv']);
    for (const name of Object.keys(g.attributes)) {
      if (!keep.has(name)) g.deleteAttribute(name);
    }
    if (g.morphAttributes) g.morphAttributes = {};
    return g;
  }

  private scatter() {
    const tune = readTune('grass-tuft', { scale: 0.6, rotX: 0, rotY: 0, rotZ: 0, offY: 0 });
    const dummy = new THREE.Object3D();
    const placements: GrassPlacement[] = this.opts.placements ?? this.generatePlacements();
    this.generatedPlacements = placements;

    for (let i = 0; i < placements.length && i < this.opts.count; i++) {
      const p = placements[i];
      const y = this.terrain.getHeight(p.x, p.z) + tune.offY;
      dummy.position.set(p.x, y, p.z);
      dummy.rotation.set(tune.rotX, p.rotY + tune.rotY, tune.rotZ);
      const s = p.scale * tune.scale;
      dummy.scale.set(s, s, s);
      dummy.updateMatrix();
      this.mesh.setMatrixAt(i, dummy.matrix);
    }
    this.mesh.count = Math.min(placements.length, this.opts.count);
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  private generatePlacements(): GrassPlacement[] {
    const size = 40;
    const n = new THREE.Vector3();
    const out: GrassPlacement[] = [];
    let attempts = 0;
    const maxAttempts = this.opts.count * 4;
    while (out.length < this.opts.count && attempts < maxAttempts) {
      attempts++;
      const x = (Math.random() * 2 - 1) * size;
      const z = (Math.random() * 2 - 1) * size;
      if (Math.hypot(x + 22, z) < 2 || Math.hypot(x - 22, z) < 2) continue;
      this.terrain.getNormal(x, z, n);
      if (n.y < 0.75) continue;
      out.push({
        x, z,
        scale: 0.75 + Math.random() * 0.5,
        rotY: Math.random() * Math.PI * 2,
      });
    }
    return out;
  }
}
